/**
 * ChatRulePreview — structured rule preview card for the assistant panel.
 *
 * Renders a governance rule proposal in the same "IF ... THEN ... for
 * agents ≤ tier" format the Policy Center page uses, with the same
 * pretty display names ("Data Classification" not "data_classification",
 * "Personal Data" not "personal_data") when a labelCatalog is provided.
 *
 * Falls back to mechanical prettification when no label matches —
 * for canonical condition fields like content.has_pii that aren't
 * governance labels.
 */

import { useTranslation } from 'react-i18next'
import { ActionBadge, type ActionType } from '@/shared/ui/atoms/action-badge'
import { TrustBadge, type TrustTier } from '@/shared/ui/atoms/trust-badge'
import { cn } from '@/shared/lib/utils'
import type { MCPRulePreview } from './mcp-client'
import type { GovernanceLabel, LabelCategory } from '@/types/governance-labels'
import { NO_VALUE_OPERATORS } from '@/services/generated/ruleOperators'

// Static display names for canonical (non-label) condition fields,
// mirrors the catalog in admin-mcp create_rule.go canonicalConditionFields.
// Used when the field isn't a `resource.labels.<x>` label-driven path.
const CANONICAL_FIELD_LABELS: Record<string, string> = {
  'resource.type': 'Resource Type',
  'resource.id': 'Resource ID',
  'resource.name': 'Resource Name',
  'resource.owner_id': 'Resource Owner',
  'agent.id': 'Agent ID',
  'agent.name': 'Agent Name',
  'agent.trust_tier': 'Agent Trust Tier',
  'agent.trust_score': 'Agent Trust Score',
  'request.action': 'Request Action',
  'request.method': 'Request Method',
  'request.path': 'Request Path',
  'mission.type': 'Mission Type',
  'mission.data_classification': 'Data Classification',
  'mission.ai_risk_level': 'AI Risk Level',
  'mission.geographic_region': 'Mission Target Region',
  'data.geographic_region': 'Data Location Region',
  'mission.lawful_basis': 'Lawful Basis',
  'mission.purposes': 'Mission Purposes',
  'mission.priority': 'Mission Priority',
  'context.environment': 'Environment',
  'context.time_hour': 'Hour of Day',
  'context.time_weekday': 'Weekday',
  'content.has_pii': 'Content has PII',
  'content.has_financial_data': 'Content has Financial Data',
  'content.has_credentials': 'Content has Credentials',
  'output.contains_pii': 'Output contains PII',
}

const OPERATOR_LABELS: Record<string, string> = {
  eq: 'is',
  ne: 'is not',
  contains: 'contains',
  starts_with: 'starts with',
  ends_with: 'ends with',
  gt: 'is greater than',
  lt: 'is less than',
  gte: 'is at least',
  lte: 'is at most',
  in: 'is one of',
  not_in: 'is not one of',
  matches: 'matches',
  exists: 'exists',
  not_exists: 'does not exist',
}

interface LabelLookup {
  byCategoryAndKey: Map<string, GovernanceLabel>
  categoryByName: Map<string, LabelCategory>
}

function buildLookup(catalog: { labels: GovernanceLabel[]; categories: LabelCategory[] } | null): LabelLookup {
  const byCategoryAndKey = new Map<string, GovernanceLabel>()
  const categoryByName = new Map<string, LabelCategory>()
  if (catalog) {
    for (const cat of catalog.categories) {
      // Categories use the snake_case `name` field as the URL segment.
      categoryByName.set((cat.name || '').toLowerCase().replace(/\s+/g, '_'), cat)
    }
    for (const label of catalog.labels) {
      const cat = catalog.categories.find(c => c.id === label.categoryId)
      if (!cat) continue
      const segment = (cat.name || '').toLowerCase().replace(/\s+/g, '_')
      byCategoryAndKey.set(`${segment}::${label.key}`, label)
    }
  }
  return { byCategoryAndKey, categoryByName }
}

function fieldDisplayName(field: string, lookup: LabelLookup): string {
  // resource.labels.<category> → category display name
  const m = field.match(/^resource\.labels\.(.+)$/)
  if (m && m[1]) {
    const cat = lookup.categoryByName.get(m[1])
    if (cat) return cat.displayName || cat.name
  }
  if (CANONICAL_FIELD_LABELS[field]) return CANONICAL_FIELD_LABELS[field]!
  // Fallback: mechanical prettification.
  return field.split('.').join(' ').replace(/_/g, ' ')
}

function valueDisplay(field: string, value: unknown, lookup: LabelLookup): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (Array.isArray(value)) {
    return value.map(v => valueDisplay(field, v, lookup)).join(', ')
  }
  if (typeof value !== 'string') return String(value)

  // resource.labels.<category> → look up the label by key and use its display name
  const m = field.match(/^resource\.labels\.(.+)$/)
  if (m && m[1]) {
    const label = lookup.byCategoryAndKey.get(`${m[1]}::${value}`)
    if (label) return label.displayName || label.key
  }
  return value
}

interface Props {
  rule: MCPRulePreview
  labelCatalog?: { labels: GovernanceLabel[]; categories: LabelCategory[] } | null
  className?: string
}

export function ChatRulePreview({ rule, labelCatalog, className }: Props) {
  const { t } = useTranslation('components', { keyPrefix: 'chatRulePreview' })
  const lookup = buildLookup(labelCatalog ?? null)
  const opLabel = OPERATOR_LABELS[rule.conditionOperator] ?? rule.conditionOperator

  return (
    <div
      className={cn(
        'rounded-xl border border-slate-200 dark:border-slate-700',
        'bg-white dark:bg-slate-900/60 px-3 py-2.5',
        'shadow-sm',
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm leading-relaxed">
        <span className="text-slate-400 dark:text-slate-500 font-medium uppercase text-xs">
          { }
          IF
        </span>
        <span className="font-medium text-slate-900 dark:text-slate-100">
          {fieldDisplayName(rule.conditionField, lookup)}
        </span>
        <span className="text-slate-500 dark:text-slate-400">{opLabel}</span>
        {!NO_VALUE_OPERATORS.includes(rule.conditionOperator) && (
          <span className="font-medium text-slate-900 dark:text-slate-100">
            {/* eslint-disable-next-line i18next/no-literal-string -- em-dash glyph as empty-value placeholder */}
            {valueDisplay(rule.conditionField, rule.conditionValue, lookup) || '—'}
          </span>
        )}

        {rule.andConditions?.map((cond, idx) => {
          const andOp = OPERATOR_LABELS[cond.operator] ?? cond.operator
          return (
            <span key={idx} className="contents">
              <span className="text-slate-400 dark:text-slate-500 font-medium uppercase text-xs">
                { }
                AND
              </span>
              <span className="font-medium text-slate-900 dark:text-slate-100">
                {fieldDisplayName(cond.field, lookup)}
              </span>
              <span className="text-slate-500 dark:text-slate-400">{andOp}</span>
              {!NO_VALUE_OPERATORS.includes(cond.operator) && (
                <span className="font-medium text-slate-900 dark:text-slate-100">
                  {/* eslint-disable-next-line i18next/no-literal-string -- em-dash glyph as empty-value placeholder */}
                  {valueDisplay(cond.field, cond.value, lookup) || '—'}
                </span>
              )}
            </span>
          )
        })}

        <span className="text-slate-400 dark:text-slate-500 font-medium uppercase text-xs">
          { }
          THEN
        </span>
        <ActionBadge action={rule.action as ActionType} />
        <span className="text-slate-500 dark:text-slate-400 text-xs">
          { }
          {t('forAgentsAtOrBelow', 'for agents ≤')}
        </span>
        <TrustBadge tier={rule.maxTrustTier as TrustTier} showIcon={false} />
      </div>
    </div>
  )
}
